import { CreditCard } from "@/view/components/CreditCard"
import { Button } from "@/view/components/ui/button"
import { DocumentType, useDocuments } from "./useDocuments"
import { Spinner } from "@/view/components/ui/spinner"
import { useRef, useState } from "react"
import { CameraIcon, FolderOpen } from "lucide-react"
import { CameraCaptureModal } from "./CameraCaptureModal"
import { DocumentCard } from "./DocumentCard"

type CameraTarget = {
  docId: string;
  side?: "frente" | "verso";
  fileName: string;
};

export const Documents = () => {
  const {
    isLoadingAttachmentsType,
    documents,
    submitDocuments,
    handleAddFile,
    isLoading
  } = useDocuments()

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [cameraTarget, setCameraTarget] = useState<CameraTarget | null>(null);

  const buildInputKey = (
    id: string,
    side?: "frente" | "verso",
  ) => `${id}${side ? `-${side}` : ""}-gallery`;

  const triggerFileInput = (
    id: string,
    side?: "frente" | "verso",
  ) => {
    fileInputRefs.current[buildInputKey(id, side)]?.click();
  };

  const openCamera = (
    doc: DocumentType,
    side?: "frente" | "verso",
  ) => {
    const baseName = doc.title?.replace(/\s+/g, "_").toLowerCase() || "documento";
    const fileName = `${baseName}${side ? `-${side}` : ""}.jpg`;
    setCameraTarget({ docId: doc.id, side, fileName });
  };

  const handleCameraCapture = (file: File) => {
    if (!cameraTarget) return;
    handleAddFile(cameraTarget.docId, file, cameraTarget.side, "camera");
  };

  const onFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentId: string,
    side?: "frente" | "verso",
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleAddFile(documentId, file, side, "gallery");
    }
    event.target.value = "";
  };

  if (isLoadingAttachmentsType) {
    return <Spinner />
  }

  const renderBox = (doc: DocumentType, side?: "frente" | "verso") => {
    const file = doc.files.find((f) => f.side === side);
    const galleryPreview = file?.source === "gallery" ? file.preview : undefined;
    const cameraPreview = file?.source === "camera" ? file.preview : undefined;
    const docId = doc.id;
    return (
      <>
        <DocumentCard
          handleClick={() => triggerFileInput(docId, side)}
          image={galleryPreview}
          labelButton="Escolher da galeria"
          icon={<FolderOpen className="h-10 w-10 text-gray-100 [&>path]:fill-primary [&>circle]:fill-white  [&>circle]:[r:4]" />}
        />
        <input
          type="file"
          ref={(el) =>
            (fileInputRefs.current[buildInputKey(docId, side)] = el)
          }
          className="hidden"
          onChange={(e) => onFileChange(e, docId, side)}
          accept="image/*,application/pdf"
        />

        <DocumentCard
          handleClick={() => openCamera(doc, side)}
          image={cameraPreview}
          labelButton="Tirar uma foto"
          icon={<CameraIcon className="h-10 w-10 text-primary [&>path]:fill-primary [&>circle]:fill-white  [&>circle]:[r:4]" />}
        />
      </>
    )
  }

  return (
    <CreditCard>
      <h1 className="text-center font-bold text-black01 text-lg sm:text-2xl leading-none mb-1 dark:text-white">Envie seus Documentos</h1>
      <p className="font-medium text-sm dark:text-gray-300">Para finalizar é <span className="text-primary font-semibold">necessário</span> anexar os arquivos!</p>


      <div className="border border-gray-400 p-4 rounded-xl my-4 flex flex-col gap-4 w-full dark:border-gray-700">

        {documents?.map((doc) => {
          const isIdDocument = doc.isIdDocument;
          const isEnergyBill = doc.isEnergyBill;
          return (
            <>
              <div key={doc.id}>
                <h1 className="text-sm font-semibold text-left pb-1 dark:text-white">{doc.title}
                  {isIdDocument && ' - Frente'}
                </h1>
                <div className="grid grid-cols-2 gap-4">
                  {isIdDocument && (
                    renderBox(doc, "frente")
                  )}
                  {isEnergyBill && (
                    renderBox(doc)
                  )}
                </div>
              </div>
              {isIdDocument && (
                <div key={doc.id + '-verso'}>
                  <h1 className="text-sm font-semibold text-left pb-1 dark:text-white">{doc.title + ' - Verso'}

                  </h1>
                  <div className="grid grid-cols-2 gap-4">
                    {renderBox(doc, "verso")}
                  </div>
                </div>
              )}


            </>
          )
        })}
      </div>

      <Button
        type="button"
        onClick={submitDocuments}
        isLoading={isLoading}
        className="rounded-lg w-full mt-4 px-10 text-white font-semibold">
        Avançar
      </Button>

      <CameraCaptureModal
        open={!!cameraTarget}
        onClose={() => setCameraTarget(null)}
        onCapture={handleCameraCapture}
        fileName={cameraTarget?.fileName}
      />
    </CreditCard>
  )
}
